/**
 * DocumentEditorPage — Single document editor (Phase 12)
 *
 * Two-pane layout: DraftEditor + UnifiedAiPanel.
 * No OutlineNavigator sidebar. Scroll-only navigation.
 * AI panel uses scope.kind = 'artifact'.
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Loader2, AlertCircle } from 'lucide-react'
import { useResponsesStore } from '@/stores/responses-store'
import { useAgentStore } from '@/stores/agent-store'
import { useAuthStore } from '@/stores/auth-store'
import { useBidReadOnly } from '@/hooks/useViewOnly'
import { useSectionAssignmentsStore } from '@/stores/section-assignments-store'
import { useDraftingStore } from '@/stores/drafting-store'
import { useCoverageStore } from '@/stores/coverage-store'
import { DraftEditor } from '@/components/drafting/DraftEditor'
import { UnifiedAiPanel } from '@/components/drafting/UnifiedAiPanel'
import type { CommentJumpContext } from '@/components/drafting/CommentCard'
import { BriefModal } from '@/components/drafting/BriefModal'
import { BriefButton } from '@/components/drafting/BriefButton'
import { NeedsFromYouBadge } from '@/components/pack/NeedsFromYouBadge'
import { ResizeHandle } from '@/components/ui/ResizeHandle'
import { useElementWidth } from '@/hooks/useElementWidth'
import { useCommentDeepLink } from '@/hooks/useCommentDeepLink'
import { useCommentsPolling } from '@/hooks/useCommentsPolling'
import { useBidMembers } from '@/hooks/useBidMembers'
import {
  resolvePaneWidths,
  maxAiPanelWidthFor,
  MIN_AI_PANEL_WIDTH,
  DEFAULT_AI_PANEL_WIDTH,
} from '@/lib/pane-layout'
import { toastApiError, type OutlineSection } from '@/lib/api'
import { DOCUMENT_SECTION_KEY } from '@/types/scope'

// ===================
// Constants
// ===================

// Pane sizing rules live in @/lib/pane-layout — shared with the section and
// questionnaire editors, which run the same shell.
const RESIZE_HANDLE_WIDTH = 4 // ResizeHandle is w-1

// ===================
// Main Component
// ===================

export function DocumentEditorPage() {
  const { bidId, artifactId } = useParams<{
    bidId: string
    artifactId: string
  }>()
  const navigate = useNavigate()
  const { search } = useLocation()

  // Consume `?commentId=` deep links from comment notifications.
  useCommentDeepLink(artifactId ?? '')
  useCommentsPolling(bidId, artifactId)

  // The whole document is on screen, so every jump is a scroll — no section
  // editor route is ever built from here.
  const commentJumpContext = useMemo<CommentJumpContext>(
    () => ({
      editorMode: 'composite',
      currentSectionKey: null,
      buildSectionPath: () => null,
      // v7's navigate returns a promise; the context is fire-and-forget.
      navigate: (path) => void navigate(path),
    }),
    [navigate],
  )

  // Stores
  const {
    selectedArtifact: artifact,
    isLoading: artifactLoading,
    error: artifactError,
    fetchArtifactById,
  } = useResponsesStore()

  const {
    isPanelOpen,
    reset: resetAgentStore,
    loadReadOnlySegments,
  } = useAgentStore()

  // The single document is one section keyed __document__. Its outline carries
  // the AI guidance + theme links the brief surface renders; loading it here
  // also hydrates the unit-brief store (notes + pins) via fetchOutline.
  const outline = useDraftingStore((s) => s.outline)
  const fetchOutline = useDraftingStore((s) => s.fetchOutline)

  // Coverage store: BriefButton + BriefModal derive the requirement count/chips
  // from it. Load it here (the inline strip used to) so the header indicator is
  // accurate before the modal opens.
  const loadCoverage = useCoverageStore((s) => s.load)

  // Local state for resizable AI panel
  const [aiPanelWidth, setAiPanelWidth] = useState(() => {
    const saved = localStorage.getItem('ai-panel-width')
    return saved ? parseInt(saved, 10) : DEFAULT_AI_PANEL_WIDTH
  })
  const [isResizing, setIsResizing] = useState(false)
  const { ref: containerRef, element: containerEl, width: containerWidth } = useElementWidth()

  // Keep the document column readable when the AI panel's stored width would
  // squeeze it (F19). Resolved here (not at render time) so the drag cap below
  // sees the width actually on screen.
  const { aiPanelWidth: renderedAiPanelWidth } = resolvePaneWidths({
    containerWidth,
    outlineWidth: null,
    aiPanelWidth: isPanelOpen ? aiPanelWidth : null,
    handlesWidth: isPanelOpen ? RESIZE_HANDLE_WIDTH : 0,
  })
  const effectiveAiPanelWidth = renderedAiPanelWidth ?? aiPanelWidth

  // Brief modal — opened from the header BriefButton.
  const [briefOpen, setBriefOpen] = useState(false)

  // The single document's one section. Prefer the persisted __document__ section
  // (carries guidance + themeIds) and fall back to a virtual section so the brief
  // surface still renders before the outline lands.
  const documentSection: OutlineSection = useMemo(() => {
    const persisted = outline?.sections.find((s) => s.sectionKey === DOCUMENT_SECTION_KEY)
    return (
      persisted ?? {
        sectionKey: DOCUMENT_SECTION_KEY,
        title: artifact?.name ?? 'Document',
        status: 'in_progress',
      }
    )
  }, [outline, artifact?.name])

  // Needed by the editor to tell "the AI wrote on my behalf" apart from "a
  // teammate edited this" when a save conflicts (agent writes are attributed to
  // the invoking user server-side).
  const currentUserId = useAuthStore((s) => s.user?.id)

  // Bid members — the comments surface needs them for @mention autocomplete and
  // commenter avatars, same as the section and questionnaire editors.
  const bidMembers = useBidMembers(bidId)

  // The single document IS the __document__ unit. The server already refuses a
  // write to a unit assigned to somebody else (lib/draft-write-guard.ts); this
  // mirrors that rule client-side so the editor stops inviting a save that
  // will fail (spec 29 §13.1 — frontend-only fix).
  const { assignments, fetchAssignments } = useSectionAssignmentsStore()
  const documentAssignment = assignments.find((a) => a.sectionId === DOCUMENT_SECTION_KEY)
  const isOwner = bidMembers.some((m) => m.userId === currentUserId && m.role === 'owner')
  const isFinalized = artifact?.status === 'finalized'
  const isInReview = artifact?.status === 'pending_review'
  const isFrozen = isFinalized || isInReview
  // A read-only user — an org viewer, or a member who is not on the bid —
  // reads the document but never opens an AI session: the session endpoint
  // refuses them, which used to leave the editor empty.
  const { isReadOnly, isLoaded: isAccessLoaded } = useBidReadOnly(bidId)
  const canWrite = useMemo(() => {
    // Read-only users never write; finalized or under-review documents are read-only for everyone
    if (isReadOnly || isFrozen) return false
    // Owner can always write
    if (isOwner) return true
    // Unassigned — no row, or a status-only row with a null user — stays open
    if (!documentAssignment || documentAssignment.userId === null) return true
    return documentAssignment.userId === currentUserId
  }, [isReadOnly, isFrozen, isOwner, documentAssignment, currentUserId])
  // A finalised or in-review document allows plain advice and no edits (Kapil
  // decision 2026-10-08, R1 E1f): the panel keeps Ask AI there, advice-only.
  // Not for a read-only user — Ask AI needs a session they cannot open.
  const adviceOnly = isReadOnly
    ? undefined
    : isFinalized ? 'finalized' as const : isInReview ? 'pending_review' as const : undefined

  // The panel's Ask AI tab is what normally loads the document's text; a
  // read-only user never mounts it, so their text is read here instead.
  useEffect(() => {
    if (!isAccessLoaded || !isReadOnly || !artifactId) return
    loadReadOnlySegments(artifactId, DOCUMENT_SECTION_KEY).catch((err: unknown) => {
      toastApiError(err, "Couldn't load this document")
    })
  }, [isAccessLoaded, isReadOnly, artifactId, loadReadOnlySegments])
  // R1 UX1: the document's state and the assignee are two different reasons;
  // passing the state as a name read "assigned to This document is under review".
  const lockedReason = isFinalized
    ? 'This document has been finalised'
    : isInReview
      ? 'This document is under review'
      : null
  const lockedByUser = !canWrite && documentAssignment
    ? (documentAssignment.fullName || documentAssignment.email)
    : null

  // Fetch data on mount
  useEffect(() => {
    if (artifactId) {
      void fetchArtifactById(artifactId)
      void fetchOutline(artifactId)
      void fetchAssignments(artifactId)
    }
    if (bidId) void loadCoverage(bidId)
  }, [artifactId, bidId, fetchArtifactById, fetchOutline, fetchAssignments, loadCoverage])

  // Guard route usage: only single-document artifacts should render this page.
  useEffect(() => {
    if (!artifact || !artifactId || !bidId) return
    if (artifact.scopeKind !== 'artifact') {
      // Keep the query string so a `?commentId=` deep link aimed at the wrong
      // editor shape still reaches the response overview.
      void navigate(`/app/bids/${bidId}/responses/${artifactId}${search}`, { replace: true })
    }
  }, [artifact, artifactId, bidId, navigate, search])

  // Reset agent state on unmount
  useEffect(() => {
    return () => {
      resetAgentStore()
    }
  }, [artifactId, resetAgentStore])

  // Persist panel width
  useEffect(() => {
    localStorage.setItem('ai-panel-width', String(aiPanelWidth))
  }, [aiPanelWidth])

  // Handle resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !containerEl) return

      const containerRect = containerEl.getBoundingClientRect()
      const newWidth = containerRect.right - e.clientX
      // Cap the drag so the document column keeps its minimum width.
      const max = maxAiPanelWidthFor(containerRect.width, 0, RESIZE_HANDLE_WIDTH)

      setAiPanelWidth(Math.max(MIN_AI_PANEL_WIDTH, Math.min(max, newWidth)))
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, containerEl])

  // Loading state
  if (artifactLoading && !artifact) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (artifactError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{artifactError}</p>
        <Button variant="ghost" onClick={() => void navigate(`/app/bids/${bidId}/write`)}>
          Back to Write
        </Button>
      </div>
    )
  }

  if (artifact && artifact.scopeKind !== 'artifact') {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden" ref={containerRef}>
      {/* Two-pane layout: Editor + AI Panel (no outline sidebar) */}
      <div className="flex flex-1 overflow-hidden">
        {/* Center Pane: Draft Editor */}
        <div className="flex-1 min-w-0 overflow-hidden flex flex-col bg-card" data-editor-pane="true">
          <BriefModal
            open={briefOpen}
            onOpenChange={setBriefOpen}
            bidId={bidId ?? ''}
            artifactId={artifactId ?? ''}
            sectionKey={DOCUMENT_SECTION_KEY}
            unitTitle={documentSection.title}
            scopeKind="artifact"
            canWrite={canWrite}
          />
          <DraftEditor
            key={artifactId ?? ''}
            bidId={bidId ?? ''}
            headerStatusSlot={
              <div className="flex items-center gap-2">
                <NeedsFromYouBadge bidId={bidId} />
                <BriefButton
                  bidId={bidId ?? ''}
                  artifactId={artifactId ?? ''}
                  sectionKey={DOCUMENT_SECTION_KEY}
                  onOpen={() => setBriefOpen(true)}
                  compact
                />
              </div>
            }
            artifactId={artifactId ?? ''}
            sectionKey={DOCUMENT_SECTION_KEY}
            section={documentSection}
            documentSectionNumber={1}
            canWrite={canWrite}
            lockedByUser={lockedByUser}
            lockedReason={lockedReason}
            currentUserId={currentUserId}
            onBack={() => void navigate(`/app/bids/${bidId}/responses/${artifactId}`)}
            headerTitle={artifact?.name ?? 'Document'}
          />
        </div>

        {/* Resize Handle */}
        {isPanelOpen && (
          <ResizeHandle
            onMouseDown={handleMouseDown}
            isResizing={isResizing}
            aria-label="Resize AI panel"
          />
        )}

        {/* Right Pane: AI Panel with artifact scope */}
        {isPanelOpen && (
          <div
            className="border-l shrink-0 overflow-hidden flex flex-col bg-muted/25"
            style={{ width: effectiveAiPanelWidth }}
            data-ai-pane="true"
          >
            <UnifiedAiPanel
              key={artifactId ?? ''}
              bidId={bidId ?? ''}
              artifactId={artifactId ?? ''}
              sectionKey={DOCUMENT_SECTION_KEY}
              scopeKind="artifact"
              scopeLabel="This document"
              bidMembers={bidMembers}
              commentJumpContext={commentJumpContext}
              // A reader of the __document__ unit assigned to somebody else
              // has nothing for the Write and Review tools to act on, so the
              // panel collapses to Comments, same as the section editor. A
              // finalised or in-review document keeps Ask AI, advice-only.
              commentsOnly={!canWrite && !adviceOnly}
              adviceOnly={adviceOnly}
            />
          </div>
        )}
      </div>
    </div>
  )
}
